import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../api/config';
import type { License, Activation, Release } from '../types';
import {
  Card,
  CardHeader,
  CardContent,
  Badge,
  PageHeader,
  Skeleton,
  EmptyState,
} from '@license-management/ui';
import {
  Key,
  Monitor,
  Download,
  ArrowRight,
  Package,
  ChevronRight,
} from 'lucide-react';

interface StatCardProps {
  name: string;
  value: number;
  total?: number;
  link: string;
  loading: boolean;
  icon: React.ReactNode;
  accentColor: 'primary' | 'accent' | 'emerald';
  index: number;
}

function StatCard({
  name,
  value,
  total,
  link,
  loading,
  icon,
  accentColor,
  index,
}: StatCardProps) {
  const accentStyles = {
    primary: {
      bg: 'bg-primary-100',
      text: 'text-primary-600',
      glow: 'group-hover:shadow-glow-primary',
      gradient: 'from-primary-500 to-primary-700',
    },
    accent: {
      bg: 'bg-accent-500/15',
      text: 'text-accent-600',
      glow: 'group-hover:shadow-glow-accent',
      gradient: 'from-accent-400 to-accent-600',
    },
    emerald: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-600',
      glow: 'group-hover:shadow-[0_4px_14px_rgba(34,197,94,0.3)]',
      gradient: 'from-emerald-400 to-emerald-600',
    },
  };

  const styles = accentStyles[accentColor];

  return (
    <Link
      to={link}
      className="group animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <Card
        interactive
        className={`h-full transition-all duration-300 ${styles.glow}`}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl ${styles.bg} ${styles.text}`}
            >
              {icon}
            </div>
            <ChevronRight className="h-5 w-5 text-zinc-300 group-hover:text-zinc-500 group-hover:translate-x-1 transition-all duration-200" />
          </div>
          <div className="mt-5">
            <p className="text-sm font-medium text-zinc-500">{name}</p>
            {loading ? (
              <Skeleton className="h-10 w-24 mt-2" />
            ) : (
              <div className="mt-2 flex items-baseline gap-2">
                <p className="text-3xl font-bold text-zinc-900 tracking-tight animate-count-up">
                  {value}
                </p>
                {total !== undefined && (
                  <span className="text-lg font-medium text-zinc-400">
                    / {total}
                  </span>
                )}
              </div>
            )}
          </div>
          {/* Subtle gradient line at bottom */}
          <div
            className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${styles.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
          />
        </CardContent>
      </Card>
    </Link>
  );
}

function DashboardPage() {
  const { data: licensesData, isLoading: licensesLoading } = useQuery({
    queryKey: ['licenses'],
    queryFn: () => apiClient.get<License[]>(API_ENDPOINTS.licenses),
  });

  const { data: activationsData, isLoading: activationsLoading } = useQuery({
    queryKey: ['activations'],
    queryFn: () => apiClient.get<Activation[]>(API_ENDPOINTS.activations),
  });

  const { data: releasesData, isLoading: releasesLoading } = useQuery({
    queryKey: ['releases'],
    queryFn: () => apiClient.get<Release[]>(API_ENDPOINTS.releases),
  });

  const activeLicenses =
    licensesData?.filter((l) => l.status === 'active').length ?? 0;
  const totalLicenses = licensesData?.length ?? 0;
  const activeActivations =
    activationsData?.filter((a) => a.status === 'active').length ?? 0;
  const availableDownloads = releasesData?.length ?? 0;

  const stats: Omit<StatCardProps, 'index'>[] = [
    {
      name: 'Active Licenses',
      value: activeLicenses,
      total: totalLicenses,
      link: '/licenses',
      loading: licensesLoading,
      icon: <Key className="h-5 w-5" />,
      accentColor: 'primary',
    },
    {
      name: 'Active Devices',
      value: activeActivations,
      link: '/activations',
      loading: activationsLoading,
      icon: <Monitor className="h-5 w-5" />,
      accentColor: 'accent',
    },
    {
      name: 'Available Downloads',
      value: availableDownloads,
      link: '/downloads',
      loading: releasesLoading,
      icon: <Download className="h-5 w-5" />,
      accentColor: 'emerald',
    },
  ];

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expired':
        return 'warning';
      case 'revoked':
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Overview of your licenses, devices, and available downloads"
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        {stats.map((stat, index) => (
          <StatCard key={stat.name} {...stat} index={index} />
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Licenses */}
        <Card
          accent="primary"
          className="animate-fade-in"
          style={{ animationDelay: '300ms' }}
        >
          <CardHeader
            title="Your Licenses"
            action={
              (licensesData?.length ?? 0) > 5 && (
                <Link
                  to="/licenses"
                  className="text-sm font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1 group"
                >
                  View all
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              )
            }
          />
          <CardContent className="p-0">
            {licensesLoading ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : licensesData?.length === 0 ? (
              <EmptyState
                icon={<Key className="h-10 w-10" />}
                title="No licenses found"
                description="Contact your administrator to get started."
                size="sm"
              />
            ) : (
              <ul className="divide-y divide-zinc-100">
                {licensesData?.slice(0, 5).map((license) => (
                  <li key={license.licenseId}>
                    <Link
                      to={`/licenses/${license.licenseId}`}
                      className="group flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-zinc-900 truncate group-hover:text-primary-600 transition-colors">
                          {license.productName}
                        </p>
                        <p className="text-sm text-zinc-500">
                          {license.licenseType} &middot;{' '}
                          {license.currentActivations}/{license.maxActivations}{' '}
                          activations
                        </p>
                      </div>
                      <Badge
                        variant={getStatusVariant(license.status)}
                        glow={license.status === 'active'}
                      >
                        {license.status}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Latest Downloads */}
        <Card
          accent="accent"
          className="animate-fade-in"
          style={{ animationDelay: '400ms' }}
        >
          <CardHeader
            title="Latest Downloads"
            action={
              (releasesData?.length ?? 0) > 5 && (
                <Link
                  to="/downloads"
                  className="text-sm font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1 group"
                >
                  View all
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              )
            }
          />
          <CardContent className="p-0">
            {releasesLoading ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : releasesData?.length === 0 ? (
              <EmptyState
                icon={<Package className="h-10 w-10" />}
                title="No downloads available"
                description="Check back later for new releases."
                size="sm"
              />
            ) : (
              <ul className="divide-y divide-zinc-100">
                {releasesData?.slice(0, 5).map((release) => (
                  <li
                    key={release.releaseId}
                    className="group flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-zinc-900">
                        {release.productName}{' '}
                        <span className="text-zinc-500 font-medium">
                          v{release.version}
                        </span>
                      </p>
                      <p className="text-sm text-zinc-500">
                        {release.platform} &middot; {release.architecture}
                      </p>
                    </div>
                    <Link
                      to="/downloads"
                      className="ml-4 text-sm font-semibold text-accent-600 hover:text-accent-700 flex items-center gap-1.5 group/link"
                    >
                      <Download className="h-4 w-4 group-hover/link:animate-bounce" />
                      Download
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DashboardPage;
